/**
 * EnterpriseSwitcher — controlled bottom sheet for switching Enterprise + Betrieb context.
 *
 * Rendered unconditionally in App.jsx; visibility controlled by open prop.
 * Trigger is the TopBar context button (not inside this component).
 *
 * Props:
 *   open            — controlled open state
 *   onOpenChange    — (open: boolean) => void
 *   token           — JWT
 *   onContextChange — ({ enterprise, betrieb }) => void
 */

import { useEffect, useState } from "react";
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';
import { Sheet } from './overlay';

/** Role → Tailwind bg class (from ROLE_BASED_NAVIGATION.md §8) */
const ROLE_BADGE_COLORS = {
  owner: 'bg-green-600',
  manager: 'bg-blue-600',
  worker: 'bg-gray-500',
  advisor: 'bg-yellow-500',
  seasonal_helper: 'bg-orange-500',
  family: 'bg-purple-600',
  contractor: 'bg-teal-600',
  vendor: 'bg-teal-600',
  buyer: 'bg-teal-600',
  driver: 'bg-teal-600',
};

/** Fetch enterprises for the authenticated user. */
async function fetchEnterprises() {
  try {
    const res = await apiClient.get('/api/v1/aif/enterprises');
    return res?.data ?? res ?? [];
  } catch { return []; }
}

/** Fetch betriebe for a specific enterprise. */
async function fetchBetriebe(enterpriseId) {
  try {
    const res = await apiClient.get(`/api/v1/aif/enterprises/${enterpriseId}/betriebe`);
    return res?.data ?? res ?? [];
  } catch { return []; }
}

export default function EnterpriseSwitcher({ open, onOpenChange, token, onContextChange }) {
  const { t } = useTranslation();
  const [enterprises, setEnterprises] = useState([]);
  const [betriebe, setBetriebe] = useState([]);
  const [selectedEnterprise, setSelectedEnterprise] = useState(null);
  const [selectedBetrieb, setSelectedBetrieb] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    (async () => {
      const ents = await fetchEnterprises();
      setEnterprises(ents);
      if (ents.length > 0) {
        const first = ents[0];
        setSelectedEnterprise(first);
        const betr = await fetchBetriebe(first.id);
        setBetriebe(betr);
        if (betr.length > 0) setSelectedBetrieb(betr[0]);
      }
      setLoading(false);
    })();
  }, [token]);

  // ── Notify parent of context ────────────────────────────────────────────────
  useEffect(() => {
    if (selectedEnterprise && selectedBetrieb && onContextChange) {
      onContextChange({
        enterprise: selectedEnterprise,
        betrieb: selectedBetrieb,
      });
    }
  }, [selectedEnterprise, selectedBetrieb]);

  const handleSelectEnterprise = async (ent) => {
    setSelectedEnterprise(ent);
    const betr = await fetchBetriebe(ent.id);
    setBetriebe(betr);
    setSelectedBetrieb(betr[0] ?? null);
    onOpenChange?.(false);
  };

  const handleSelectBetrieb = (b) => {
    setSelectedBetrieb(b);
    onOpenChange?.(false);
  };

  return (
    <Sheet.Root open={open} onOpenChange={onOpenChange}>
      <Sheet.Content side="bottom" size="md" title={t('switcher.betrieb')}>
        <Sheet.Body>
          {loading ? (
            <div className="py-8 text-center text-[var(--aif-text-muted)] text-sm">
              {t('ui.loading', 'Laden...')}
            </div>
          ) : (
            <div className="space-y-4" data-testid="enterprise-sheet">
              {/* Enterprises section */}
              <div>
                <p className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
                  {t('switcher.enterprise')}
                </p>
                <div className="space-y-1">
                  {enterprises.map((ent) => (
                    <button
                      key={ent.id}
                      onClick={() => handleSelectEnterprise(ent)}
                      className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${
                        selectedEnterprise?.id === ent.id
                          ? 'bg-[var(--aif-color-primary)] text-white font-medium'
                          : 'hover:bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)]'
                      }`}
                      data-testid="enterprise-option"
                    >
                      {ent.name}
                      {ent.my_role === 'owner' && (
                        <span className="ml-2 text-xs opacity-70">{t('switcher.owner')}</span>
                      )}
                      {selectedEnterprise?.id === ent.id && (
                        <span className="ml-1 text-xs opacity-70">✓</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Betriebe section */}
              {betriebe.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold text-[var(--aif-text-muted)] uppercase tracking-wide mb-2">
                    {t('switcher.betrieb')}
                  </p>
                  <div className="space-y-1">
                    {betriebe.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => handleSelectBetrieb(b)}
                        className={`w-full text-left px-3 py-2.5 rounded-xl text-sm transition-colors ${
                          selectedBetrieb?.id === b.id
                            ? 'bg-[var(--aif-color-primary)] text-white font-medium'
                            : 'hover:bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)]'
                        }`}
                        data-testid="betrieb-option"
                      >
                        {b.name}
                        {b.total_ha && (
                          <span className="ml-2 text-xs opacity-70">{b.total_ha} ha</span>
                        )}
                        {selectedBetrieb?.id === b.id && (
                          <span className="ml-1 text-xs opacity-70">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Sheet.Body>
      </Sheet.Content>
    </Sheet.Root>
  );
}
